/**
 * JourneyInstanceSelector Component
 *
 * Rich dropdown selector for switching between journey instances.
 * Includes country code badges, sponsorship info, expiration dates,
 * info button with details modal, and family-member "Citizen Only" badge.
 *
 * Based on mockup design at mockups/journey-web/ for parity.
 */

import {
  Button,
  CountryFlag,
  CountryFlagPair,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  PanelFooter,
  PanelFooterLinkContent,
  cn,
  panelFooterLinkVariants,
} from '@tonnie/ui';
import { useLocale, useTranslation, useCountryName } from '@tonnie/locale';
import {
  Calendar,
  ChevronDown,
  Info,
  Loader2,
  MapPin,
  Plus,
  RotateCcw,
  Rocket,
  Settings,
} from '@tonnie/assets';
import { useMyArchivedJourneyCount } from '@tonnie/sdk';
import { useState } from 'react';
import { Link } from 'wouter';
import type { JourneyInstance } from '@/core/contexts/AuthContext';
import { useJourneyInstance } from '@/core/contexts/JourneyInstanceContext';
import { featureFlags } from '@/core/config/feature-flags';
import { JourneyDetailsModal } from './JourneyDetailsModal';
import {
  JourneyAccessBadges,
  isJourneyExpired,
  journeyExpirationDate,
} from './JourneyAccessBadges';
import { JourneyCompanies } from './JourneyCompanies';
import { JourneyNameLine } from './JourneyNameLine';
import { JourneyRouteTile } from './JourneyRouteTile';

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

interface IJourneyInstanceSelectorProps {
  className?: string;
}

export function JourneyInstanceSelector({ className }: IJourneyInstanceSelectorProps) {
  const { t } = useTranslation();
  const countryName = useCountryName();
  const { activeInstance, instances, isLoading, setActiveInstance } = useJourneyInstance();
  const [selectedInstanceForDetails, setSelectedInstanceForDetails] =
    useState<JourneyInstance | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const displayInstance = activeInstance || instances[0];
  const hasNoJourneys = !isLoading && instances.length === 0;

  // W8.1 · F47 — the zero-journey state offers "recover an archived journey"
  // only when there is one to recover.
  const { data: archivedSummary } = useMyArchivedJourneyCount();
  const archivedCount = archivedSummary?.archivedCount ?? 0;

  // ── Trigger button label ─────────────────────────────────────────────
  const renderTriggerContent = () => {
    // State 1: Still loading from API
    if (isLoading) {
      return (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin text-muted-foreground" />
          <span className="hidden md:inline text-muted-foreground">{t('common.loading')}</span>
          <span className="md:hidden text-muted-foreground">...</span>
        </>
      );
    }

    // State 2: No journeys — prompt user to build
    if (hasNoJourneys) {
      return (
        <>
          <Rocket className="w-4 h-4 mr-2 text-primary-accessible" />
          <span className="hidden md:inline">{t('journey.selector.buildOne')}</span>
          <span className="md:hidden">{t('journey.selector.buildOneShort')}</span>
          <ChevronDown className="w-4 h-4 ml-2" />
        </>
      );
    }

    // State 3: Has journeys — show active instance with flag
    // origin_country is nullable now (pending_setup citizens have no
    // origin yet); show '?' as the placeholder so the pill renders
    // without erroring instead of "undefined → PT".
    const origin = displayInstance?.originCountry ?? '?';
    const destination = displayInstance?.destinationCountry ?? '?';
    // The pill used to be a template string of two flag GLYPHS. It is JSX now
    // because `<CountryFlag>` renders an <img>, which no string can hold.
    return (
      <>
        <MapPin className="w-4 h-4 mr-2" />
        {displayInstance ? (
          <>
            <span className="hidden md:inline-flex items-center gap-1.5">
              <CountryFlag code={origin} size="sm" />
              <span>{origin}</span>
              <span aria-hidden="true" className="text-muted-foreground">
                →
              </span>
              <CountryFlag code={destination} size="sm" />
              <span>{destination}</span>
            </span>
            {/* Mobile drops the ISO codes, so the flags are the only route
                identifier — hence the screen-reader label here and not above. */}
            <CountryFlagPair
              className="md:hidden"
              originCode={origin}
              destinationCode={destination}
              size="sm"
              routeLabel={t('common.routeSrLabel', {
                origin: countryName(displayInstance.originCountry),
                destination: countryName(displayInstance.destinationCountry),
              })}
            />
          </>
        ) : (
          <>
            <span className="hidden md:inline">{t('common.loading')}</span>
            <span className="md:hidden">...</span>
          </>
        )}
        <ChevronDown className="w-4 h-4 ml-2" />
      </>
    );
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className={className} data-testid="journey-selector-trigger">
            {renderTriggerContent()}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-96 p-0">
          <div className="p-3">
            {/* Loading state */}
            {isLoading && (
              <div className="flex items-center justify-center gap-2 p-6">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t('journey.selector.loadingJourneys')}
                </span>
              </div>
            )}

            {/* Empty state — no journeys */}
            {hasNoJourneys && (
              <div className="p-4 space-y-3 text-center" data-testid="journey-selector-empty">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <MapPin className="w-6 h-6 text-primary-accessible" />
                </div>
                <div>
                  <p className="text-sm font-medium">{t('journey.selector.noJourneysTitle')}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('journey.selector.noJourneysDescription')}
                  </p>
                </div>
                {/*
                THE ROUTE BACK. (W8.1 · F47 · D-29)

                D-29 allows a citizen to archive their LAST journey, and the
                reason it is safe is entirely this block. The builder always
                renders, so nobody is ever stranded without a way to make a new
                journey — the actual gap the board found was that there was no
                way back to an OLD one. Without this the citizen archives their
                only journey, lands on an empty state offering "build one", and
                their CheckUp, TelePortal and Care progress is reachable by no
                route in the product.

                Shown only when `archivedCount > 0`, so a citizen who has never
                archived anything is not offered a section that would be empty
                — the same rule the "Arquivadas (n)" section follows.
              */}
                {archivedCount > 0 && (
                  <Link
                    href="/journeys/manage"
                    data-testid="journey-selector-recover-archived"
                    className="inline-flex items-center gap-2 text-xs font-medium text-primary-accessible hover:underline"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    {t('journey.manage.recoverArchived', { count: archivedCount })}
                  </Link>
                )}
              </div>
            )}

            {/* Journey list */}
            {!isLoading && instances.length > 0 && (
              <div className="space-y-2">
                {instances.map((instance) => (
                  <JourneyCard
                    key={instance.id}
                    instance={instance}
                    isActive={instance.id === activeInstance?.id}
                    onSelect={() => {
                      // setActiveInstance is async — it awaits any registered
                      // switch guards (e.g. the FlowLifecycleManager's leave
                      // prompt) and may resolve `false` if the user opts to
                      // stay. We don't need to act on the result here; the
                      // dropdown just closes either way and the active
                      // journey is the source of truth for the selected
                      // state.
                      void setActiveInstance(instance);
                    }}
                    onInfoClick={
                      featureFlags.ENABLE_JOURNEY_DETAILS
                        ? () => {
                            setSelectedInstanceForDetails(instance);
                            setDetailsModalOpen(true);
                          }
                        : undefined
                    }
                  />
                ))}
              </div>
            )}

            {/* Separator only when there are journeys above the build button */}
            {!isLoading && instances.length > 0 && <DropdownMenuSeparator className="my-2" />}

            {/* Build New Journey CTA — always available (journeys start as basic/limited) */}
            {!isLoading && (
              <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                <Link
                  href="/journey/builder?new=1"
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-lg border-2 border-dashed border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-sm font-medium group"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Plus className="w-5 h-5 text-primary-accessible" />
                  </div>
                  <span>
                    {instances.length === 0
                      ? t('journey.selector.buildFirst')
                      : t('journey.selector.buildNew')}
                  </span>
                </Link>
              </DropdownMenuItem>
            )}

            {/*
            MANAGEMENT LEAVES THE DROPDOWN. (W8.1 · F47, design item 3)

            The dropdown stays a fast switcher — compact rows, an active mark,
            one link out. Archive, restore and "leave this programme" are
            destructive or consequential and belong on a page with room for the
            consequence copy, the Undo affordance and the archived section.
            Putting them behind a kebab inside a switcher row is how the
            business Pipeline ended up archiving the wrong journey seven times.
          */}
          </div>
          {/* The panel's one link out, in the shared footer every header panel
              uses (the bell's "Open inbox" is the same row). Sticky, so a long
              list of journeys never scrolls it out of reach. */}
          {!isLoading && (
            <PanelFooter className="sticky bottom-0 bg-popover">
              <DropdownMenuItem asChild className={panelFooterLinkVariants()}>
                <Link href="/journeys/manage" data-testid="journey-selector-manage">
                  <PanelFooterLinkContent icon={Settings}>
                    {t('journey.manage.manageJourneys')}
                  </PanelFooterLinkContent>
                </Link>
              </DropdownMenuItem>
            </PanelFooter>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Journey Details Modal (feature-flagged) */}
      {featureFlags.ENABLE_JOURNEY_DETAILS && (
        <JourneyDetailsModal
          instance={selectedInstanceForDetails}
          open={detailsModalOpen}
          onOpenChange={setDetailsModalOpen}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// JOURNEY CARD (extracted for readability)
// ═══════════════════════════════════════════════════════════════════════

interface IJourneyCardProps {
  instance: JourneyInstance;
  isActive: boolean;
  onSelect: () => void;
  onInfoClick?: () => void;
}

function JourneyCard({ instance, isActive, onSelect, onInfoClick }: IJourneyCardProps) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const countryName = useCountryName();

  const expirationDate = journeyExpirationDate(instance);

  return (
    <DropdownMenuItem
      onClick={onSelect}
      data-testid="journey-selector-card"
      data-journey-id={instance.id}
      data-active={isActive ? 'true' : 'false'}
      className={cn(
        'cursor-pointer p-0 focus:bg-transparent focus:outline-none',
        'data-[highlighted]:bg-transparent'
      )}
    >
      <div
        className={cn(
          'relative w-full p-4 rounded-xl border-2 transition-all duration-200',
          'hover:shadow-md hover:scale-[1.01]',
          isActive
            ? 'border-primary bg-primary/5 shadow-sm'
            : 'border-border bg-card hover:border-primary/30'
        )}
      >
        {/* Top Right — Active dot + Info button */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {isActive && <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />}
          {onInfoClick && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onInfoClick();
              }}
              className="shrink-0 p-1.5 rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
              aria-label={t('journey.details.title')}
              title={t('journey.details.title')}
            >
              <Info className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-3 pr-8">
          <div className="flex items-center gap-3">
            <JourneyRouteTile
              originCountry={instance.originCountry}
              destinationCountry={instance.destinationCountry}
            />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm leading-tight">
                {countryName(instance.destinationCountry)}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {instance.originCountry
                  ? t('journey.selector.from', { country: countryName(instance.originCountry) })
                  : t('journey.selector.fromUnknown')}
              </div>
            </div>
          </div>

          {/* What tells two journeys on one route apart (R8): its name, or
           * its company or scenario, and the day it started. */}
          <JourneyNameLine
            label={instance.label}
            companyNames={instance.companies.map((c) => c.name)}
            scenario={instance.scenario}
            startedAt={instance.activatedAt ?? instance.createdAt}
          />

          {/* The companies that hold this journey — every one the server lists,
           * whoever pays: one as its logo and name, several as stacked logos
           * with a hover card listing each (D-19). "Invited by" or "Shared
           * with" is the server's `tierBadge.inviterAttributed` (F47). The
           * access badges below say who PAYS; this says who can SEE it. */}
          <JourneyCompanies
            companies={instance.companies}
            invited={instance.tierBadge?.inviterAttributed === true}
            relationshipTestId="journey-company-relationship"
            focusable={false}
            className="pt-1"
          />

          <JourneyAccessBadges instance={instance} />

          {expirationDate && !isJourneyExpired(instance) && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              <span>
                {t('journey.selector.expiresOn')} {formatDate(expirationDate)}
              </span>
            </div>
          )}
        </div>
      </div>
    </DropdownMenuItem>
  );
}
